/**
 * Training SOP — PPR Coordinator track.
 *
 * For the PprCoordinator role (`ppr.manage`): the person who starts a Post
 * Project Review when a job closes, chases the nine survey responses, and
 * produces the report.
 *
 * ⚠️ Facts here that are easy to get wrong, verified against the code
 * (2026-08-18 sweep of PprDashboard/PprJobDetail/StartPprModal/
 * EmailGeneratorModal/CancelReviewActions, ppr.controller, ppr.service):
 *   • The Financial Data tab is 21 fields — two code comments still say 26;
 *     the field allowlist, the form and the wiring spec all agree on 21.
 *   • The tracker NEVER sends mail. Generate Email builds text to paste;
 *     reminders/escalations go out via Power Automate.
 *   • Cancelled is a second terminal status, deliberately NOT in the Stage
 *     dropdown — only Cancel review reaches it, because it captures a reason
 *     and re-arms the close-out alert.
 *   • Accounting's `ppr.financials.edit` covers the 21 fields plus exactly one
 *     stage move (Awaiting Financials → Financials Completed by Accounting).
 *   • Spelling: the module's own labels are "Cancelled" / "Show cancelled" —
 *     bold label references keep the app's spelling (same rule as the pour
 *     cards and bid statuses), prose stays US English.
 */
import { OWNERSHIP, type Track } from '../types';

export const PPR_TRACK: Track = {
  id: 'ppr',
  label: 'PPR Coordinator',
  audience:
    'Whoever runs Post Project Reviews — starting the review when a job closes, collecting the nine responses, and producing the report.',
  suggestedFor: { anyRole: ['PprCoordinator'] },
  /**
   * Track-level gate, same reason the Administrator track has one: every topic
   * inside carries a PPR permission rule, so a reader with no PPR access who
   * selected this track got the heading with an empty body — which reads like
   * a loading failure, not a closed door (Codex, this PR). Anyone who can use
   * the module holds ppr.view or ppr.manage; Admin passes via the bypass.
   *
   * hideFromFieldLeaderOnly too, mirroring App.tsx: flGuard redirects a
   * field-leader-only user away from /ppr BEFORE the permission check runs, so
   * a FieldLeader + PprCoordinator without office access cannot reach the
   * screen this track teaches (Codex, #353, third round — and the same rule
   * the mod-ppr appendix entry already carries).
   */
  access: { anyPermission: ['ppr.view', 'ppr.manage'], hideFromFieldLeaderOnly: true },
  sections: [
    // ── 1. The pipeline ─────────────────────────────────────────────────────
    {
      id: 'ppr-pipeline',
      title: 'The pipeline',
      intro:
        'Every review on one board, one column per stage. The board is the to-do list.',
      topics: [
        {
          id: 'ppr-board',
          title: 'The PPR board',
          summary:
            'Top navigation ▸ PPR lays reviews out as a pipeline: one column per stage, one card per review, with response counts, age and escalation flags on the card.',
          steps: [
            'Watch the three counters: **Active PPRs**, **Awaiting Financials**, and **Overdue Responses** — the last turns red the moment anything is overdue.',
            'Search by job number or name, filter by review type, and use **Show completed** / **Show cancelled** to bring finished work back into view.',
            'Click a card to open the review.',
          ],
          watchOut: [
            'Finished reviews are hidden by default — a review that "disappeared" has almost certainly reached **Complete** or **Cancelled**.',
          ],
          deepLink: '/ppr',
          access: { anyPermission: ['ppr.view', 'ppr.manage'], hideFromFieldLeaderOnly: true },
        },
        {
          id: 'ppr-start',
          title: 'Starting a review',
          summary:
            'A review starts from **+ Start PPR**: pick the job, confirm its details, and confirm the nine contacts. The job value picks the review type — under $250K is **Financial Only**, at or above is **Full Review** — and you can override it.',
          steps: [
            'Use **+ Start PPR** and search for the job — the picker searches every job, not just ones assigned to you.',
            'Check what pre-fills — job number, name, close date, SharePoint folder, and the people it could find — and type the **Job Value (USD)** yourself: it is not pre-filled, it is required, and it is what picks the review type.',
            'Review the nine contact rows — the positions are fixed and always present, and a name or email you do not know yet can stay blank and be filled in later from the **Contacts & Notifications** tab.',
            'On a low-value job with an extraordinary event, check **President consulted** and record the notes.',
          ],
          watchOut: [
            'The nine positions are fixed: Field Leader, Estimator, Project Manager, Safety Manager, Survey Manager, Scheduler, Operations, Payroll, and Business Development/Sales.',
            'An automated close-out alert flags each job as it approaches its estimated completion — normally once per job. Cancelling or deleting a review can re-arm it, and the dialog tells you whether it actually did. Starting the review is still yours to do.',
          ],
          access: { anyPermission: ['ppr.manage'] },
        },
        {
          id: 'ppr-stages',
          title: 'The stages, and the one gate',
          summary:
            'A review moves Initiating → Survey Collection → Awaiting Financials → Financials Completed by Accounting → Report Prep → Complete, set from the **Stage** dropdown on the review. Only the coordinator moves a review freely; anyone else who can open PPR is offered exactly one move — **Awaiting Financials** to **Financials Completed by Accounting**, and only while the review sits there.',
          watchOut: [
            'You cannot move to **Report Prep** or **Complete** until the financials are marked complete — the refusal message points you at the Financial Data tab.',
            '**Cancelled** is deliberately not in the dropdown. Stopping a review goes through **Cancel review**, which records a reason.',
          ],
          // ppr.view too: the status endpoint genuinely grants that holder the
          // single Awaiting Financials → Financials Completed by Accounting
          // move (scoped in the service), so hiding all stage guidance from
          // them omitted a live mutation they can perform (Codex, #353,
          // round nine).
          access: { anyPermission: ['ppr.view', 'ppr.manage'] },
        },
      ],
    },

    // ── 2. Collecting responses ─────────────────────────────────────────────
    {
      id: 'ppr-responses',
      title: 'Collecting the nine responses',
      topics: [
        {
          id: 'ppr-contacts',
          title: 'Tracking who has answered',
          summary:
            'The **Contacts & Notifications** tab keeps the nine positions with their notified and responded dates. The tab badge counts responses, and a contact goes into warning once four days pass without an answer.',
          steps: [
            'Use **Mark Notified** / **Mark Responded** for anything that happened outside the tracker, and the Unmark links to correct a slip.',
            'Use **Edit** to fix a name or email — people change between job start and close-out.',
            'Record the field leader\'s **feedback on their team** in the card at the top of the tab.',
          ],
          watchOut: [
            'The team-feedback card is a **separate deliverable** from the field leader\'s own survey response — completing one does not complete the other.',
            'Responses normally arrive on their own (next topics) — mark by hand only what genuinely happened off the books.',
          ],
          access: { anyPermission: ['ppr.manage'] },
        },
        {
          id: 'ppr-generate-email',
          title: 'Generating the request emails',
          summary:
            'The tracker writes the email but never sends it. **✉ Generate Email** builds a subject and body from the position\'s own template, with the deadline defaulted three business days out.',
          steps: [
            'Use **✉ Generate Email** on the contact.',
            'For the Field Leader, the Good Catch data is pulled in automatically, ready to be included.',
            '**Copy subject** and **Copy body** into a new email, or use **✉ Open in Outlook**.',
            'Leave **Mark this contact as notified** checked, and finish with **Done** — clicking Done is what records the notification and starts the response clock. Copying, opening Outlook, or closing the modal any other way records nothing.',
          ],
          watchOut: [
            'On a long email, **Open in Outlook** can truncate the body — the modal warns you when that risk exists. Copy and paste instead.',
          ],
          access: { anyPermission: ['ppr.manage'] },
        },
        {
          id: 'ppr-responses-arrive',
          title: 'Responses arrive on their own',
          summary:
            'The surveys are RedeApp forms. A submission lands in the tracker automatically and flips the right contact to responded — each feedback form maps to its position, and the general survey reads the position from its own dropdown.',
          watchOut: [
            'Reminders and escalations are automated too: a contact five days silent is escalated to you. A reminder already handed to the mailer cannot be recalled — it still goes out even if you cancel the review in the gap.',
          ],
          access: { anyPermission: ['ppr.manage'] },
        },
      ],
    },

    // ── 3. Financials and the report ────────────────────────────────────────
    {
      id: 'ppr-money-report',
      title: 'Financials and the report',
      topics: [
        {
          id: 'ppr-financials',
          title: 'The Financial Data tab',
          summary:
            'Twenty-one figures, from contract value to labor hours. Accounting enters them while the review sits in **Awaiting Financials**; the coordinator can enter or correct them too.',
          steps: [
            'Enter percentages as plain percent — 12.34, not 0.1234. The margin dollar fields show a live percent-of-contract check as you type.',
            '**Save changes** first — **Mark Financials Complete** stays disabled until the tab is saved.',
            'Marking complete is what unlocks **Report Prep** and **Complete**.',
          ],
          watchOut: [
            'An accounting login covers the financial fields and exactly one stage move — **Awaiting Financials** to **Financials Completed by Accounting**. Everything else on the review is the coordinator\'s.',
          ],
          owner: OWNERSHIP.pprFinancials,
          // ppr.financials.edit too: Accounting holds ppr.view + that, and this
          // is the topic explaining the tab they own — hiding it from them
          // removed the instructions from the very people named as the owner
          // (Codex, this PR).
          access: { anyPermission: ['ppr.manage', 'ppr.financials.edit'] },
        },
        {
          id: 'ppr-report',
          title: 'Producing the report',
          summary:
            'The **Report** tab lays the review out on screen, and **⬇ Download Report Template (.docx)** produces the Word document: financial data pre-filled, narrative sections left as blank headings for you to write after reading the survey responses.',
          steps: [
            'Download the template once the responses and financials are in.',
            'Write the narrative sections in Word — the tracker deliberately does not draft them.',
            'Use **🖨 Print** for a paper copy — it prints just the report, without the app around it.',
          ],
          watchOut: [
            '**Full Review** and **Financial Only** produce different layouts — the review type on the card decides which you get.',
          ],
          access: { anyPermission: ['ppr.view', 'ppr.manage'] },
        },
      ],
    },

    // ── 4. Stopping and records ─────────────────────────────────────────────
    {
      id: 'ppr-stopping',
      title: 'Stopping a review, and the paper trail',
      topics: [
        {
          id: 'ppr-cancel-reopen',
          title: 'Cancel, reopen, delete',
          summary:
            'A mis-started or abandoned review is **cancelled**, not deleted — the record and any responses keep. Delete exists only while a review is still in **Initiating** with nobody notified and nobody responded.',
          steps: [
            '**Cancel review** asks for a reason, kept as a read-only cancellation record on the review — every viewer sees it. It also disables the contact actions, so a stopped review cannot email nine people.',
            '**Reopen review** returns it to **Initiating** — the contacts keep whatever was already notified and answered.',
          ],
          watchOut: [
            'Cancelling does not always re-arm the close-out alert — a completion date already passed is the common blocker, and the dialog tells you which case you are in. If it will not re-fire, track the job for close-out yourself.',
          ],
          access: { anyPermission: ['ppr.manage'] },
        },
        {
          id: 'ppr-notes-history',
          title: 'Notes & History',
          summary:
            'Coordinator notes live on the review, alongside a small history: who created it, the last update, when financials completed, and the outcome.',
          // The cancellation reason is NOT in the notes: PprService.cancel
          // writes cancelled_reason into its own columns and CancellationRecord
          // renders it (the "no longer reads `notes`" note in ppr.service.ts).
          // The tab's own on-screen warning still blames cancellation — stale
          // app copy from the old behavior; flagged for a follow-up fix.
          watchOut: [
            'If the notes changed elsewhere while you were typing — another session editing the same review — saving would overwrite that. Copy your text, reload, and re-apply it.',
          ],
          access: { anyPermission: ['ppr.manage'] },
        },
      ],
    },
  ],
};
